import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'

import { formatProduct } from '@/lib/formatProduct'
import { fetchRest } from '@/lib/hook/tanstackQuery'
import { consumeSearchClickHeaders } from '@/lib/sessionTracking'

const isBrowser = typeof window !== 'undefined'

// Cle de cache partagee entre le prefetch SSR (getInitialProps) et le hook client.
// La forme de la cle doit etre stable (slugs tries) pour profiter de l'hydratation.
// La locale fait varier la reponse (Accept-Language) -> incluse dans la cle.
export const productBySlugKey = (
  slugs: string[],
  skeyProduct?: string | null,
  locale?: string | null
) => ['productBySlug', [...slugs].sort(), skeyProduct ?? null, locale ?? null] as const

/**
 * Reproduit fidelement l'ancien resolver getProductBySlug :
 * 1. seo?type=p&slugs[]=... -> object_id (sinon slug brut)
 * 2. products/{id}?skeyProduct=... pour chaque id (Promise.allSettled)
 * 3. filtre les rejets / sans product_id, applique formatProduct
 */
export const fetchProductsBySlug = async (
  slugs: string[],
  skeyProduct?: string | null,
  locale?: string,
  customHeaders?: Record<string, string>,
  baseUrl?: string,
  // true = vraie consultation de fiche produit (le back ne compte une vue
  // session-analytics QUE dans ce cas ; l'enrichissement des blocs recommend
  // passe par ce meme fetch sans le marqueur)
  pdpView = false
): Promise<any[] | null> => {
  if (!slugs || slugs.length === 0) return null

  // Cause racine des 404 recurrents (PHARMEDISTORE-BK2/BMA/BNB) sur les slugs
  // accentues : le filtre CS-Cart `seo?slugs[]=<slug>` matche la forme Unicode
  // stockee de maniere exacte. L'URL du navigateur fournit le slug en NFC
  // (é = U+00E9), or CS-Cart peut avoir stocke la forme NFD (e + U+0301) — ou
  // l'inverse. Quand les formes different, la reponse SEO ne contient AUCUNE
  // entree `type=='p'` : le match cote client (meme normalise) n'a alors rien a
  // selectionner et on retombe sur `slugOrId` -> `products/<slug>` -> 404.
  // Les correctifs precedents ne normalisaient que la comparaison cote client,
  // sans corriger la requete sortante -> le bug persistait. On envoie donc les
  // deux formes (NFC + NFD) dans `slugs[]` pour que CS-Cart matche quelle que
  // soit la forme stockee. Un produit reellement absent ne renvoie toujours
  // aucune entree `p` et conserve son 404 legitime.
  const slugVariants = Array.from(
    new Set(slugs.flatMap(s => [String(s).normalize('NFC'), String(s).normalize('NFD')]))
  )
  const slugGet = slugVariants.map(s => `slugs[]=${encodeURIComponent(s)}`)
  const seoEndpoint = `seo?type=p&${slugGet.join('&')}&skeyProduct=${encodeURIComponent(String(skeyProduct))}`

  const target = await fetchRest<Record<string, any>>({
    endpoint: seoEndpoint,
    locale,
    customHeaders,
    baseUrl,
  })

  // Le slug vient de l'URL decodee (router.query.slug) : selon la source, les
  // caracteres accentues peuvent etre en NFC (é = U+00E9) tandis que le `name`
  // renvoye par CS-Cart peut etre en NFD (e + U+0301) — ou l'inverse. Une egalite
  // stricte echoue alors sur les slugs accentues (ex. "précommande-..."), on
  // retombe sur `|| slugOrId` -> products/<slug> -> 404. On normalise les deux
  // cotes en NFC avant comparaison (no-op pour l'ASCII).
  const toNfc = (v: unknown) => String(v ?? '').normalize('NFC')

  // L'endpoint est deja interroge avec le slug demande (`seo?type=p&slugs[]=<slug>`) :
  // la reponse est scopee a ce(s) slug(s). Le match strict sur `t.name` reste
  // fragile (differences Unicode/casse/espaces au-dela du NFC), et son fallback
  // `|| slugOrId` renvoie le slug brut a `products/<slug>` -> 404 systematique
  // (cause racine du 404 Sentry). On aligne donc sur le resolver canonique du
  // reste du code (seoChain / usePage / categoryNameBySlug) : a defaut de match
  // exact sur `name`, on retient l'entree de type `p` renvoyee par le SEO.
  // Un produit reellement absent (aucune entree type `p`) retombe sur `slugOrId`
  // et conserve son 404 legitime.
  const productId = slugs.map(slugOrId => {
    const entries = Object.values(target).filter((t: any) => t.type == 'p')
    const exact = entries.find((t: any) => toNfc(t.name) === toNfc(slugOrId))
    return (exact ?? entries[0])?.object_id || slugOrId
  })

  if (!productId || productId.length === 0) return null

  // Session analytics : si la navigation vient d'un resultat de recherche
  // (< 30 s), le fetch de la fiche envoie X-Pharme-Src/X-Pharme-Rank
  // (consomme une seule fois, no-op cote SSR).
  const clickHeaders = consumeSearchClickHeaders()

  const viewHeaders: Record<string, string> = pdpView ? { 'X-Pharme-View': 'pdp' } : {}

  const productsPromise = productId.map(id =>
    fetchRest<any>({
      endpoint: `products/${encodeURIComponent(String(id))}?skeyProduct=${encodeURIComponent(String(skeyProduct))}`,
      locale,
      customHeaders: { ...clickHeaders, ...viewHeaders, ...customHeaders },
      baseUrl,
    })
  )
  const products = await Promise.allSettled(productsPromise)

  return products
    .filter(s => s.status !== 'rejected' && (s as PromiseFulfilledResult<any>)?.value?.product_id)
    .map((item: any) => formatProduct(item.value))
}

const useProduct = (_slug: string | string[], productId = null, skeyProduct = null): any => {
  const router = useRouter()
  const locale = (router.query.locale as string) || 'fr'

  const slug = typeof _slug === 'string' ? [_slug] : _slug
  const querySlugs = productId ? [productId] : slug

  const { data, isPending, isError, error } = useQuery({
    queryKey: productBySlugKey(querySlugs ?? [], skeyProduct, locale),
    // pdpView=true : le hook n'est consomme que par les composants de la fiche
    // produit — la vue session-analytics n'est comptee que sur ce chemin.
    queryFn: () => fetchProductsBySlug(querySlugs, skeyProduct, locale, undefined, undefined, true),
    enabled: !!_slug,
    // Iso-comportement avec l'ancien Apollo fetchPolicy :
    // client = cache-first (staleTime long), SSR = network (refetch a froid).
    staleTime: isBrowser ? 5 * 60 * 1000 : 0,
  })

  // `isPending` et NON `isLoading` : en TanStack Query v5, `isLoading` vaut
  // `isPending && isFetching`. Il retombe donc a `false` alors qu'aucune donnee
  // n'est encore resolue des que le fetch n'est pas physiquement en vol :
  //  - `fetchStatus === 'paused'` (navigateur hors-ligne / coupure reseau),
  //  - query desactivee (`enabled: false` tant que le slug n'est pas connu).
  // Le consommateur enchainait alors sur `!product` et affichait la page
  // "produit introuvable" (+ captureMessage "404 : ...") pour un produit qui
  // existe bel et bien. `isPending` couvre pending + paused et ne redevient
  // false qu'une fois la query reellement resolue (succes ou erreur).
  // Meme correctif que useCompanyBySlug.
  const loading = isPending

  if (!data) return { loading, product: null, isError, error }

  return {
    loading,
    product: typeof _slug === 'string' ? data[0] : data,
    isError,
    error,
  }
}

export default useProduct
